import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { imgSrc } from '../lib/imgSrc';
import SubmitMaintenanceRequestDialog from '../components/SubmitMaintenanceRequestDialog';
import HeaderMenu from '../components/HeaderMenu';
import AcknowledgeRequestDialog from '../components/AcknowledgeRequestDialog';
import { DeleteRequestConfirm } from '../components/DeleteRequestConfirm';
import { Trash2 } from 'lucide-react';
import { RequestOutcomeLink } from '../components/RequestOutcomeLink';
import { requestOutcomeLabel } from '../lib/historyList';

const STATUS_META = {
  not_started: { label: 'Awaiting Acknowledgement', cls: 'bg-amber-100 text-amber-900' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-800' },
  complete: { label: 'Complete', cls: 'bg-green-100 text-green-800' },
};
const statusLabel = (req) => (req.status === 'complete' && requestOutcomeLabel({ request_outcome: req.outcome }))
  || (STATUS_META[req.status] || STATUS_META.not_started).label;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const UNASSIGNED_ID = 'unassigned';

const LocationMaintenanceRequests = ({ user, onLogout }) => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const isUnassignedView = locationId === UNASSIGNED_ID;

  const [location, setLocation] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open'); // 'open' (not_started + in_progress), 'all', or specific status
  const [showSubmit, setShowSubmit] = useState(false);
  const [acknowledging, setAcknowledging] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const role = user?.role;
  const canSubmit = role === 'admin' || role === 'technician' || role === 'operator';
  const canManage = role === 'admin' || role === 'technician';

  const fetchRequests = async () => {
    try {
      if (isUnassignedView) {
        // Special "Unassigned Equipment" view — no real location doc to fetch.
        // Pull the master request list and filter to null plant_location_id.
        const reqRes = await axios.get(`${API}/maintenance-requests`);
        setLocation({ id: UNASSIGNED_ID, name: 'Unassigned Equipment' });
        setRequests((reqRes.data || []).filter((r) => !r.plant_location_id));
        return;
      }
      const [locRes, reqRes] = await Promise.all([
        axios.get(`${API}/locations/${locationId}`),
        axios.get(`${API}/locations/${locationId}/maintenance-requests`),
      ]);
      setLocation(locRes.data);
      setRequests(reqRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load maintenance requests');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [locationId]);

  const handleReopen = async (req) => {
    try {
      await axios.put(`${API}/maintenance-requests/${req.id}/status`, { status: 'not_started' });
      toast.success('Re-opened');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAcknowledged = () => {
    setAcknowledging(null);
    fetchRequests();
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/maintenance-requests/${deletingId}`);
      toast.success('Maintenance request deleted');
      setDeletingId(null);
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete request');
    }
  };

  const filtered = requests.filter((r) => {
    if (filter === 'all') return true;
    if (filter === 'open') return r.status !== 'complete';
    return r.status === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-600">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center flex-wrap gap-3">
            <div>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="mb-2"
                data-testid="back-to-dashboard-btn"
              >
                ← Back to Plants
              </Button>
              <h1 className="text-2xl font-bold text-slate-900">
                Maintenance Requests — {location?.name}
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                {requests.length} total ·{' '}
                {requests.filter((r) => r.status !== 'complete').length} awaiting acknowledgement ·{' '}
                {requests.filter((r) => r.status === 'complete').length} acknowledged
              </p>
            </div>
            <div className="flex gap-2">
              {canSubmit && (
                <Button onClick={() => setShowSubmit(true)} data-testid="submit-request-page-btn">
                  + Submit Request
                </Button>
              )}
              <HeaderMenu user={user} onLogout={onLogout} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-slate-600 mr-2">Filter:</span>
          {[
            { v: 'open', label: 'Awaiting Acknowledgement' },
            { v: 'complete', label: 'Acknowledged' },
            { v: 'all', label: 'All' },
          ].map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setFilter(f.v)}
              data-testid={`req-filter-${f.v}`}
              className={
                'text-xs rounded-full px-3 py-1 border ' +
                (filter === f.v
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 hover:bg-slate-50')
              }
            >
              {f.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-slate-600">
            {requests.length === 0
              ? 'No maintenance requests for this location yet.'
              : 'No requests match this filter.'}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((req) => {
              const meta = STATUS_META[req.status] || STATUS_META.not_started;
              return (
                <div
                  key={req.id}
                  className="bg-white rounded-lg shadow p-4 border"
                  data-testid={`req-row-${req.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-900">
                          {req.equipment_name}
                        </span>
                        <span className={`text-xs rounded px-2 py-0.5 font-medium ${meta.cls}`}>
                          {statusLabel(req)}
                        </span>
                      </div>
                      <div className="text-xs text-slate-600 mt-1">
                        Submitted {formatDate(req.submitted_at)} by {req.submitted_by_name}
                        {req.observed_at && req.observed_at !== req.submitted_at && (
                          <> · observed {formatDate(req.observed_at)}</>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {canManage && req.status !== 'complete' && (
                        <Button size="sm" onClick={() => setAcknowledging(req)} data-testid={`acknowledge-${req.id}`}>
                          Acknowledge
                        </Button>
                      )}
                      {canManage && req.status === 'complete' && !req.outcome && (
                        <Button size="sm" variant="outline" onClick={() => handleReopen(req)} data-testid={`reopen-${req.id}`}>
                          Re-open
                        </Button>
                      )}
                      {canManage && (
                        <Button size="sm" variant="ghost" onClick={() => setDeletingId(req.id)} data-testid={`delete-request-${req.id}`}>
                          <Trash2 size={14} className="mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{req.description}</div>

                  {Array.isArray(req.images) && req.images.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {req.images.map((img, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => window.open(imgSrc(img), '_blank')}
                          className="block w-20 h-20 rounded overflow-hidden border hover:border-slate-400 cursor-pointer"
                          title="Click to view full-size"
                        >
                          <img
                            src={imgSrc(img, { thumb: true })}
                            alt={`Request image ${idx + 1}`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  {req.status === 'in_progress' && req.started_by_name && (
                    <div className="text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1 mt-3">
                      Started {formatDate(req.started_at)} by {req.started_by_name}
                    </div>
                  )}
                  {req.status === 'complete' && (
                    <div className="text-sm bg-green-50 border border-green-200 rounded px-3 py-2 mt-3">
                      <div className="text-xs text-green-900" data-testid={`req-outcome-${req.id}`}>
                        {req.outcome ? 'Acknowledged' : 'Completed'} {formatDate(req.completed_at)} by {req.completed_by_name}
                      </div>
                      <RequestOutcomeLink outcome={req.outcome} reference={req.outcome_reference}
                        recordId={req.outcome_record_id} machineryId={req.outcome_machinery_id}
                        testId={`req-outcome-link-${req.id}`} />
                      {req.completion_notes && (
                        <div className="text-sm mt-1">
                          <span className="font-medium">Completion notes:</span> {req.completion_notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SubmitMaintenanceRequestDialog
        open={showSubmit}
        defaultLocationId={locationId}
        onClose={() => setShowSubmit(false)}
        onSuccess={fetchRequests}
      />

      {deletingId && <DeleteRequestConfirm onCancel={() => setDeletingId(null)} onConfirm={handleDelete} />}
      {acknowledging && (
        <AcknowledgeRequestDialog
          request={acknowledging}
          onClose={() => setAcknowledging(null)}
          onDone={handleAcknowledged}
        />
      )}
    </div>
  );
};

export default LocationMaintenanceRequests;
